/**
 * PACKAGES DATA - SINGLE SOURCE OF TRUTH
 *
 * This file contains all package data for the Apex Experience booking system.
 * Packages are pre-configured combinations of lodges and activities.
 *
 * Note: Full package data is stored in Firestore (packages collection).
 * This file provides static data for SSG (generateStaticParams) and fallback display.
 *
 * @see types/booking.ts for Package interface
 * @see lib/migrations/init-firestore.ts for Firestore data
 */

import { getLodgeById, type Lodge } from './lodges';
import { type Currency } from '@/types/booking';

export interface PackageActivity {
  activityId: string;
  activityName: string;
  quantity: number;
  day: number;
}

export interface PackageData {
  id: string;
  slug: string;
  name: string;
  nameLocalized: {
    en: string;
    es: string;
  };
  description: string;
  descriptionLocalized: {
    en: string;
    es: string;
  };
  lodgeId: string;
  lodgeName: string;
  activities: PackageActivity[];
  duration: number;
  price: number; // in cents
  currency: Currency;
  extraGuestPrice: number; // in cents
  minGuests: number;
  maxGuests: number;
  inclusions: string[];
  exclusions: string[];
  seasonStart: string; // YYYY-MM-DD
  seasonEnd: string; // YYYY-MM-DD
  active: boolean;
  featured: boolean;
  sortOrder: number;
  heroImage: string;
  location: string;
  subtitle: string;
}

/**
 * Static package data for SSG and display
 */
export const packages: PackageData[] = [
  {
    id: 'ski-week-parva',
    slug: 'ski-week-parva',
    name: 'Ski Week at La Parva',
    nameLocalized: {
      en: 'Ski Week at La Parva',
      es: 'Semana de Esqui en La Parva',
    },
    description:
      'Experience a full week of skiing at La Parva with accommodation at Apex Mountain Penthouse. Enjoy world-class slopes, stunning Andes views, and the ultimate ski-in/ski-out experience.',
    descriptionLocalized: {
      en: 'Experience a full week of skiing at La Parva with accommodation at Apex Mountain Penthouse. Enjoy world-class slopes, stunning Andes views, and the ultimate ski-in/ski-out experience.',
      es: 'Experimenta una semana completa de esqui en La Parva con alojamiento en Apex Mountain Penthouse. Disfruta de pistas de clase mundial, impresionantes vistas de los Andes y la experiencia definitiva de ski-in/ski-out.',
    },
    lodgeId: 'apex-mountain-penthouse',
    lodgeName: 'Apex Mountain Penthouse',
    activities: [
      { activityId: 'resort-skiing', activityName: 'Resort Skiing', quantity: 5, day: 1 },
      { activityId: 'ski-touring', activityName: 'Ski Touring', quantity: 1, day: 4 },
    ],
    duration: 7,
    price: 750000, // $7,500
    currency: 'USD',
    extraGuestPrice: 150000, // $1,500 per extra guest
    minGuests: 2,
    maxGuests: 6,
    inclusions: [
      '7 nights accommodation at Apex Mountain Penthouse',
      '5 days resort skiing with lift passes',
      '1 day ski touring with expert guide',
      'Daily gourmet breakfast',
      'Private airport transfers',
      'Ski-in/ski-out access',
    ],
    exclusions: [
      'International flights',
      'Ski equipment rental',
      'Lunch and dinner',
      'Travel insurance',
      'Personal expenses',
    ],
    seasonStart: '2025-06-01',
    seasonEnd: '2025-09-30',
    active: true,
    featured: true,
    sortOrder: 1,
    heroImage: '/images/lodges/apex-mountain-penthouse/IMG_2891.webp',
    location: 'La Parva, Chilean Andes',
    subtitle: 'The ultimate ski-in/ski-out mountain experience',
  },
  {
    id: 'heli-adventure-patagonia',
    slug: 'heli-adventure-patagonia',
    name: 'Heli-Skiing Adventure in Patagonia',
    nameLocalized: {
      en: 'Heli-Skiing Adventure in Patagonia',
      es: 'Aventura de Heliski en Patagonia',
    },
    description:
      'Ultimate heli-skiing experience in the remote peaks of Patagonia. Access pristine untracked powder in volcanic terrain accessible only by helicopter from our exclusive Yelcho lodge.',
    descriptionLocalized: {
      en: 'Ultimate heli-skiing experience in the remote peaks of Patagonia. Access pristine untracked powder in volcanic terrain accessible only by helicopter from our exclusive Yelcho lodge.',
      es: 'La experiencia definitiva de heliski en las remotas cumbres de la Patagonia. Accede a nieve virgen en terreno volcanico accesible solo en helicoptero desde nuestro exclusivo lodge Yelcho.',
    },
    lodgeId: 'apex-yelcho-patagonia',
    lodgeName: 'Apex Yelcho Patagonia',
    activities: [
      { activityId: 'heli-skiing', activityName: 'Heli-skiing', quantity: 4, day: 1 },
    ],
    duration: 5,
    price: 1200000, // $12,000
    currency: 'USD',
    extraGuestPrice: 250000, // $2,500 per extra guest
    minGuests: 4,
    maxGuests: 6,
    inclusions: [
      '5 nights accommodation at Apex Yelcho Patagonia',
      '4 days heli-skiing (weather permitting)',
      'All gourmet meals and beverages',
      'Helicopter transfers from Chaiten',
      'Avalanche safety equipment',
      'Expert mountain guides',
      'Après-ski activities',
    ],
    exclusions: [
      'Flights to Chaiten',
      'Ski equipment rental',
      'Travel insurance',
      'Alcoholic beverages',
      'Gratuities',
    ],
    seasonStart: '2025-07-15',
    seasonEnd: '2025-09-15',
    active: true,
    featured: true,
    sortOrder: 2,
    heroImage: '/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif',
    location: 'Patagonia, Chile',
    subtitle: 'Untouched powder in the worlds most remote terrain',
  },
  {
    id: 'fly-fishing-escape',
    slug: 'fly-fishing-escape',
    name: 'Fly Fishing Escape',
    nameLocalized: {
      en: 'Fly Fishing Escape',
      es: 'Escapada de Pesca con Mosca',
    },
    description:
      'World-class fly fishing on pristine Patagonian rivers. Cast for trophy rainbow and brown trout with expert guides on 12km of private river access at our exclusive fishing lodge.',
    descriptionLocalized: {
      en: 'World-class fly fishing on pristine Patagonian rivers. Cast for trophy rainbow and brown trout with expert guides on 12km of private river access at our exclusive fishing lodge.',
      es: 'Pesca con mosca de clase mundial en los pristinos rios de la Patagonia. Pesca truchas arcoiris y marrones trofeo con guias expertos en 12km de acceso privado al rio en nuestro exclusivo lodge de pesca.',
    },
    lodgeId: 'apex-patagonia-lodge',
    lodgeName: 'Apex Patagonia Lodge',
    activities: [
      { activityId: 'fly-fishing', activityName: 'Fly Fishing', quantity: 4, day: 1 },
    ],
    duration: 5,
    price: 550000, // $5,500
    currency: 'USD',
    extraGuestPrice: 120000, // $1,200 per extra guest
    minGuests: 2,
    maxGuests: 6,
    inclusions: [
      '5 nights accommodation at Apex Patagonia Lodge',
      '4 days guided fly fishing',
      'All gourmet meals',
      'Premium fishing equipment',
      'Flies and tackle',
      'Transportation to fishing spots',
      'Waders and boots',
    ],
    exclusions: [
      'International flights',
      'Fishing license',
      'Gratuities',
      'Travel insurance',
      'Personal fishing gear',
    ],
    seasonStart: '2025-11-01',
    seasonEnd: '2026-04-30',
    active: true,
    featured: false,
    sortOrder: 3,
    heroImage: '/images/lodges/apex-patagonia-lodge/Copia-de-6Las-Gualas--768x576.avif',
    location: 'Patagonia, Chile',
    subtitle: 'Trophy trout on 12km of private river',
  },
  {
    id: 'lake-district-adventure',
    slug: 'lake-district-adventure',
    name: 'Lake District Adventure',
    nameLocalized: {
      en: 'Lake District Adventure',
      es: 'Aventura en el Distrito de los Lagos',
    },
    description:
      'Discover the natural beauty of Chiles Lake District with fly fishing, water activities, and lakeside relaxation. Stay at our tranquil lakefront retreat with 200m of private waterfront.',
    descriptionLocalized: {
      en: 'Discover the natural beauty of Chiles Lake District with fly fishing, water activities, and lakeside relaxation. Stay at our tranquil lakefront retreat with 200m of private waterfront.',
      es: 'Descubre la belleza natural del Distrito de los Lagos de Chile con pesca con mosca, actividades acuaticas y relajacion junto al lago. Alojate en nuestro tranquilo refugio frente al lago con 200m de costa privada.',
    },
    lodgeId: 'apex-lakefront-serenity',
    lodgeName: 'Apex Lakefront Serenity',
    activities: [
      { activityId: 'fly-fishing', activityName: 'Fly Fishing', quantity: 2, day: 2 },
      { activityId: 'water-skiing', activityName: 'Water Skiing', quantity: 1, day: 3 },
    ],
    duration: 4,
    price: 380000, // $3,800
    currency: 'USD',
    extraGuestPrice: 80000, // $800 per extra guest
    minGuests: 2,
    maxGuests: 10,
    inclusions: [
      '4 nights accommodation at Apex Lakefront Serenity',
      '2 days guided fly fishing',
      '1 day water skiing session',
      'All gourmet meals',
      'Kayaks and paddleboards',
      'Private lakefront access',
    ],
    exclusions: [
      'International flights',
      'Ground transportation',
      'Travel insurance',
      'Alcoholic beverages',
    ],
    seasonStart: '2025-10-01',
    seasonEnd: '2026-03-31',
    active: true,
    featured: false,
    sortOrder: 4,
    heroImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
    location: 'Lake District, Chile',
    subtitle: 'Tranquil lakeside adventures and relaxation',
  },
  {
    id: 'ultimate-luxury-experience',
    slug: 'ultimate-luxury-experience',
    name: 'Ultimate Luxury Experience',
    nameLocalized: {
      en: 'Ultimate Luxury Experience',
      es: 'Experiencia de Lujo Definitiva',
    },
    description:
      'The pinnacle of mountain luxury with helicopter access, heli-skiing, and VIP resort skiing. Experience 5-star all-inclusive service at our premium summit lodge at 3,000m elevation.',
    descriptionLocalized: {
      en: 'The pinnacle of mountain luxury with helicopter access, heli-skiing, and VIP resort skiing. Experience 5-star all-inclusive service at our premium summit lodge at 3,000m elevation.',
      es: 'La cuspide del lujo de montana con acceso en helicoptero, heliski y esqui VIP en resort. Experimenta servicio todo incluido 5 estrellas en nuestro lodge premium de cumbre a 3,000m de elevacion.',
    },
    lodgeId: 'apex-premium-lodge',
    lodgeName: 'Apex Premium Lodge',
    activities: [
      { activityId: 'heli-skiing', activityName: 'Heli-skiing', quantity: 2, day: 1 },
      { activityId: 'resort-skiing', activityName: 'VIP Resort Skiing', quantity: 3, day: 3 },
    ],
    duration: 6,
    price: 2000000, // $20,000
    currency: 'USD',
    extraGuestPrice: 400000, // $4,000 per extra guest
    minGuests: 2,
    maxGuests: 8,
    inclusions: [
      '6 nights in Presidential Suite',
      '2 days private heli-skiing',
      '3 days VIP resort skiing with private instructor',
      'Helicopter transfers from Santiago',
      'All gourmet meals and premium beverages',
      'Private butler service',
      'Spa treatments',
      'Wine cellar experience',
    ],
    exclusions: [
      'International flights',
      'Personal ski equipment',
      'Gratuities',
    ],
    seasonStart: '2025-06-15',
    seasonEnd: '2025-09-30',
    active: true,
    featured: true,
    sortOrder: 5,
    heroImage: 'https://images.unsplash.com/photo-1605540436563-5bca919ae766?w=1920&q=80',
    location: 'Chilean Andes',
    subtitle: 'The pinnacle of mountain luxury',
  },
];

/**
 * Get package by slug
 */
export function getPackageBySlug(slug: string): PackageData | undefined {
  return packages.find((pkg) => pkg.slug === slug);
}

/**
 * Get package by ID
 */
export function getPackageById(id: string): PackageData | undefined {
  return packages.find((pkg) => pkg.id === id);
}

/**
 * Get featured packages
 */
export function getFeaturedPackages(): PackageData[] {
  return packages
    .filter((pkg) => pkg.featured && pkg.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Get active packages
 */
export function getActivePackages(): PackageData[] {
  return packages
    .filter((pkg) => pkg.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Get lodge data for a package
 */
export function getPackageLodge(pkg: PackageData): Lodge | undefined {
  return getLodgeById(pkg.lodgeId);
}

/**
 * Format price in cents to display string
 */
export function formatPackagePrice(cents: number, currency: string = 'USD'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
